// Pure aggregation for the "Standard Reports" section on the Reports page
// (src/components/dashboard/StandardReports.jsx). Kept side-effect-free so it can
// be unit-tested without a DOM or the DB — the component does the I/O (loads the
// services list + the customer-type category map) and the filtering UI, then calls
// these to build the two report tables.
//
// Report 1 — Number of Shelters: per-state count of DFV Services onboarded on or
//   before the From date (col1) and on or before the To date (col2). Registration
//   is shelters.date_onboarded (John, 2 Sep 2026); services with no onboarding date
//   can't be placed in time so are excluded. Only State + the two dates apply.
// Report 2 — Children Assisted by Category: per-state, per-category count of
//   children receiving that category, the approved $ cost, and the average per
//   child. Optionally broken down by each category's sub-type.

import { REQUEST_STATUSES } from "../utils/enums";
import { calcUniItems, feeTypeLabel } from "../utils/formConstants";
import { decisionMatch, isStationeryFee, requestDecisionTotals } from "../utils/helpers";

// The five reporting categories, in display order (Stationery is split out of Fees,
// matching the Statistics dashboard).
export const STANDARD_CATEGORIES = [
  { key: "stationery", label: "Stationery" },
  { key: "fees", label: "Fees" },
  { key: "uniforms", label: "Uniforms" },
  { key: "shoes", label: "Shoes" },
  { key: "devices", label: "Devices" },
];

const STATE_ORDER = ["QLD", "NSW", "VIC", "WA", "SA", "TAS", "NT", "ACT"];

// States in the fixed portal order, then anything else alphabetically.
export function orderStates(states) {
  return [...states].sort((a, b) => {
    const ia = STATE_ORDER.indexOf(a);
    const ib = STATE_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a < b ? -1 : a > b ? 1 : 0;
  });
}

// What a single child contributes to each category for the chosen decision.
// Returns, per category key, either null (the child is not receiving that category)
// or { cost, subs } where subs maps a sub-type label -> its cost for this child.
// "Receiving" mirrors the Statistics dashboard: an approved item of that category
// (a uniform/shoes/device object with an approved status, or an approved fee).
export function childCategoryContribution(child, decision) {
  const out = { stationery: null, fees: null, uniforms: null, shoes: null, devices: null };
  const c = child || {};

  // Fees — Stationery is its own category (isStationeryFee: Stationery, Stationery &
  // Textbooks, Textbooks — each its own sub-line); every other fee type is "Fees".
  for (const f of c.fees || []) {
    if (!decisionMatch(f.approvalStatus, decision)) continue;
    const amt = parseFloat(f.feeAmount) || 0;
    if (isStationeryFee(f.feeType)) {
      out.stationery = out.stationery || { cost: 0, subs: {} };
      out.stationery.cost += amt;
      const sub = f.feeType;
      out.stationery.subs[sub] = (out.stationery.subs[sub] || 0) + amt;
    } else {
      out.fees = out.fees || { cost: 0, subs: {} };
      out.fees.cost += amt;
      const lbl = feeTypeLabel(f.feeType || "Other");
      out.fees.subs[lbl] = (out.fees.subs[lbl] || 0) + amt;
    }
  }

  // Uniforms — cost via calcUniItems; sub-types are the standard + custom item names.
  if (c.uniforms && decisionMatch(c.uniforms.approvalStatus, decision)) {
    const u = c.uniforms;
    const subs = {};
    const items = u.items || {};
    for (const k of Object.keys(items)) {
      const it = items[k] || {};
      const q = Number(it.qty) || 0;
      const cost = parseFloat(it.cost) || 0;
      if (q > 0) subs[k] = (subs[k] || 0) + q * cost;
    }
    for (const d of u.customItems || []) {
      const nm = (d?.name || "").trim();
      const q = Number(d?.qty) || 0;
      const cost = parseFloat(d?.cost) || 0;
      if (nm && q > 0) subs[nm] = (subs[nm] || 0) + q * cost;
    }
    out.uniforms = { cost: calcUniItems([u]), subs };
  }

  // Shoes — junior joggers + senior leather school shoes.
  if (c.shoes && decisionMatch(c.shoes.approvalStatus, decision)) {
    const s = c.shoes;
    const jq = Number(s.jQty) || 0;
    const lq = Number(s.lQty) || 0;
    const j = jq * (parseFloat(s.jCost) || 0);
    const l = lq * (parseFloat(s.lCost) || 0);
    const subs = {};
    if (jq > 0) subs.Joggers = j;
    if (lq > 0) subs["Leather shoes"] = l;
    out.shoes = { cost: j + l, subs };
  }

  // Devices — a single device per child; sub-type is the device type (Laptop/iPad).
  if (c.laptop && decisionMatch(c.laptop.approvalStatus, decision)) {
    const cost = parseFloat(c.laptop.estimatedCost) || 0;
    const t = c.laptop.deviceType || "Device";
    out.devices = { cost, subs: { [t]: cost } };
  }

  return out;
}

// Report 2 aggregation. `requests` should already be date-filtered by the caller;
// this applies the State + Customer-type-category filters and groups by state.
// Returns { [catKey]: { byState: {state: {children, cost, subs}}, rows, totals } }.
//   rows    — states in portal order, each { state, children, cost, subs }
//   totals  — { children, cost } summed across the shown states
//   subs    — { [subLabel]: { children, cost } } within a state
export function childrenByCategory(requests, opts = {}) {
  const { decision = "approved", state = "All", customerCategory = "", categoryByCode = {} } = opts;

  const byStateFor = {};
  for (const cat of STANDARD_CATEGORIES) byStateFor[cat.key] = {};

  for (const r of requests || []) {
    if (state !== "All" && (r.state || "") !== state) continue;
    if (customerCategory && (categoryByCode[r.shelterCode] || "") !== customerCategory) continue;
    const st = r.state || "—";
    for (const child of r.children || []) {
      const contrib = childCategoryContribution(child, decision);
      for (const cat of STANDARD_CATEGORIES) {
        const d = contrib[cat.key];
        if (!d) continue;
        if (!byStateFor[cat.key][st]) {
          byStateFor[cat.key][st] = { children: 0, cost: 0, subs: {} };
        }
        const cell = byStateFor[cat.key][st];
        cell.children += 1;
        cell.cost += d.cost;
        for (const [sub, subCost] of Object.entries(d.subs)) {
          if (!cell.subs[sub]) cell.subs[sub] = { children: 0, cost: 0 };
          const sc = cell.subs[sub];
          sc.children += 1;
          sc.cost += subCost;
        }
      }
    }
  }

  const out = {};
  for (const cat of STANDARD_CATEGORIES) {
    const byState = byStateFor[cat.key];
    const rows = orderStates(Object.keys(byState)).map((st) => ({ state: st, ...byState[st] }));
    const totals = rows.reduce(
      (t, r) => ({ children: t.children + r.children, cost: t.cost + r.cost }),
      { children: 0, cost: 0 }
    );
    out[cat.key] = { byState, rows, totals };
  }
  return out;
}

// Report 1 aggregation. Per-state count of services onboarded on/before the From
// date (col1) and on/before the To date (col2, defaulting to today when To is
// blank). Deactivated services are excluded by default; services with no
// date_onboarded are excluded (can't be placed in time). A blank From date leaves
// col1 at 0 (no baseline).
export function shelterCountsByState(shelters, fromDate, toDate, opts = {}) {
  const { includeDeactivated = false, state = "All", today } = opts;
  const end = toDate || today || new Date().toISOString().slice(0, 10);
  const byState = {};
  for (const s of shelters || []) {
    if (!includeDeactivated && s.deactivated) continue;
    if (state !== "All" && (s.state || "") !== state) continue;
    const onb = (s.date_onboarded || "").slice(0, 10);
    if (!onb) continue;
    const st = s.state || "—";
    if (!byState[st]) byState[st] = { col1: 0, col2: 0 };
    const cell = byState[st];
    if (fromDate && onb <= fromDate) cell.col1 += 1;
    if (onb <= end) cell.col2 += 1;
  }
  const rows = orderStates(Object.keys(byState)).map((st) => ({ state: st, ...byState[st] }));
  const totals = rows.reduce((t, r) => ({ col1: t.col1 + r.col1, col2: t.col2 + r.col2 }), {
    col1: 0,
    col2: 0,
  });
  return { rows, totals };
}

// The set of shelter_codes that have at least one ACTIVE-login user who has signed in
// at least once (drives the "shelters with a signed-in user" figures + Report 5).
export function signedInShelterCodes(memberships = [], deactivatedByUser = {}, activation = {}) {
  const codes = new Set();
  for (const m of memberships) {
    if (!m.user_id) continue;
    if (deactivatedByUser[m.user_id]) continue;
    if (activation[m.user_id]) codes.add(m.shelter_code);
  }
  return codes;
}

// Report 3 — Active Users. A CURRENT snapshot (John: last-day is fine): per state,
// the count of active login caseworkers (a user_shelters membership WITH a login,
// whose profile is not deactivated) and — of those — how many have signed in at
// least once (the "Live" status). A user is counted once per state they hold an
// active membership in (multi-service users appear in each of their states; the
// TOTAL row is the column sum, so it can exceed the distinct headcount).
// Two service-level columns are added (John, 9 Sep 2026): the count of ACTIVE DFV
// services per state, and — of those — how many have at least one signed-in login user.
//   memberships       — listAllUserShelters() rows (active-only by default)
//   deactivatedByUser — { user_id: boolean } from the shelter profiles
//   activation        — { user_id: last_sign_in_at | null } from the admin EF
//   stateByCode       — { shelter_code: state }
//   shelters          — listAllShelters() rows (for the two service-level columns)
export function usersByState(opts = {}) {
  const {
    memberships = [],
    deactivatedByUser = {},
    activation = {},
    stateByCode = {},
    shelters = [],
    state = "All",
    includeDeactivated = false,
  } = opts;
  const signedCodes = signedInShelterCodes(memberships, deactivatedByUser, activation);
  const byState = {};
  const ensure = (st) => {
    if (!byState[st]) {
      byState[st] = {
        logins: new Set(),
        signedIn: new Set(),
        activeShelters: 0,
        sheltersSignedIn: 0,
      };
    }
    return byState[st];
  };
  // Login users — counted per state via the service's state.
  for (const m of memberships) {
    if (!m.user_id) continue; // login accounts only (login-less contacts skipped)
    if (deactivatedByUser[m.user_id]) continue; // active login accounts only
    const st = stateByCode[m.shelter_code] || "—";
    if (state !== "All" && st !== state) continue;
    const cell = ensure(st);
    cell.logins.add(m.user_id);
    if (activation[m.user_id]) cell.signedIn.add(m.user_id);
  }
  // Active services per state + how many have a signed-in user.
  for (const s of shelters) {
    if (!includeDeactivated && s.deactivated) continue;
    const st = s.state || "—";
    if (state !== "All" && st !== state) continue;
    const cell = ensure(st);
    cell.activeShelters += 1;
    if (signedCodes.has(s.shelter_code)) cell.sheltersSignedIn += 1;
  }
  const rows = orderStates(Object.keys(byState)).map((st) => ({
    state: st,
    logins: byState[st].logins.size,
    signedIn: byState[st].signedIn.size,
    activeShelters: byState[st].activeShelters,
    sheltersSignedIn: byState[st].sheltersSignedIn,
  }));
  const totals = rows.reduce(
    (t, r) => ({
      logins: t.logins + r.logins,
      signedIn: t.signedIn + r.signedIn,
      activeShelters: t.activeShelters + r.activeShelters,
      sheltersSignedIn: t.sheltersSignedIn + r.sheltersSignedIn,
    }),
    { logins: 0, signedIn: 0, activeShelters: 0, sheltersSignedIn: 0 }
  );
  return { rows, totals };
}

// Report 5 — Shelters Without an Activated Login (John, 9 Sep 2026). Per state, the
// ACTIVE DFV services whose shelter_code has NO active-login user that has signed in
// yet (nobody at that service has logged in). Each state lists its services by name.
//   shelters/memberships/deactivatedByUser/activation — as for usersByState.
export function sheltersWithoutActivatedLogin(opts = {}) {
  const {
    shelters = [],
    memberships = [],
    deactivatedByUser = {},
    activation = {},
    state = "All",
    includeDeactivated = false,
  } = opts;
  const signedCodes = signedInShelterCodes(memberships, deactivatedByUser, activation);
  const byState = {};
  for (const s of shelters) {
    if (!includeDeactivated && s.deactivated) continue;
    const st = s.state || "—";
    if (state !== "All" && st !== state) continue;
    if (signedCodes.has(s.shelter_code)) continue; // has a signed-in user → not listed
    if (!byState[st]) byState[st] = [];
    byState[st].push({
      name: s.shelter_name || s.name || s.shelter_code || "—",
      code: s.shelter_code || "",
    });
  }
  const rows = orderStates(Object.keys(byState)).map((st) => ({
    state: st,
    services: byState[st].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)),
  }));
  const total = rows.reduce((t, r) => t + r.services.length, 0);
  return { rows, total };
}

// Report 4 — Number of Requests. Per state: how many SUBMITTED (non-draft) requests
// fall in the period, their total approved (per-decision) cost, and the average cost
// per request. `requests` is already date-filtered by the caller; this applies the
// State + Customer-type-category filters (Category does NOT apply — this is a
// whole-request rollup) and drops drafts.
export function requestsByState(requests, opts = {}) {
  const { decision = "approved", state = "All", customerCategory = "", categoryByCode = {} } = opts;
  const byState = {};
  for (const r of requests || []) {
    if ((r.status || "") === REQUEST_STATUSES.DRAFT) continue; // submitted requests only
    if (state !== "All" && (r.state || "") !== state) continue;
    if (customerCategory && (categoryByCode[r.shelterCode] || "") !== customerCategory) continue;
    const st = r.state || "—";
    if (!byState[st]) byState[st] = { count: 0, cost: 0 };
    byState[st].count += 1;
    byState[st].cost += requestDecisionTotals(r, decision).total;
  }
  const rows = orderStates(Object.keys(byState)).map((st) => ({ state: st, ...byState[st] }));
  const totals = rows.reduce((t, r) => ({ count: t.count + r.count, cost: t.cost + r.cost }), {
    count: 0,
    cost: 0,
  });
  return { rows, totals };
}
